import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChatLoginPage from './ChatLoginPage';
import ChatApp from './ChatApp';
import LoadingScreen from './LoadingScreen';
import ErrorBoundary from './ErrorBoundary';
import { hasDashboard } from '../services/roles';

// The admin dashboard pulls in Recharts and the chart components. Loading it
// only when an admin opens an admin route keeps it out of the chat bundle.
const AdminApp = lazy(() => import('./AdminApp'));

const ADMIN_TAB_KEYS = ['faq', 'users', 'documents', 'feedback', 'usage', 'analytics'];
const LANGUAGE_PATHS = ['en', 'jp'];

function normalizeLanguagePath(langCode) {
  return LANGUAGE_PATHS.includes(langCode) ? langCode : 'jp';
}

function AdminIndexRedirect() {
  const { langCode } = useParams();
  const languagePath = normalizeLanguagePath(langCode);
  return <Navigate to={`/${languagePath}/admin/faq`} replace />;
}

function LegacyAdminTabRedirect() {
  const { adminTab } = useParams();
  const tab = ADMIN_TAB_KEYS.includes(adminTab) ? adminTab : 'faq';
  // デフォルト日本語版の管理画面URLへ戻す
  return <Navigate to={`/jp/admin/${tab}`} replace />;
}

function LoginRoute() {
  const { langCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const languagePath = normalizeLanguagePath(langCode);
  if (langCode !== languagePath) return <Navigate to={`/${languagePath}/login`} replace />;
  if (user) return <Navigate to={`/${languagePath}`} replace />;
  return <ChatLoginPage
    languagePath={languagePath}
    onChangeLanguage={(nextLanguage) => navigate(`/${nextLanguage}/login`)}
  />;
}

function ProtectedRoute({ children }) {
  const { user, isAuthChecked } = useAuth();
  const { langCode } = useParams();
  if (!isAuthChecked) return <LoadingScreen />;
  if (!user) return <Navigate to={`/${normalizeLanguagePath(langCode)}/login`} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAuthChecked } = useAuth();
  const { langCode } = useParams();
  if (!isAuthChecked) return <LoadingScreen />;
  if (!user) return <Navigate to={`/${normalizeLanguagePath(langCode)}/login`} replace />;
  if (!hasDashboard(user)) return <Navigate to="/" replace />;
  return children;
}

export default function AppShell() {
  const { isAuthChecked } = useAuth();

  if (!isAuthChecked) return <LoadingScreen />;

  return (
    <Routes>
      <Route
        path="/login"
        element={<Navigate to="/jp/login" replace />}
      />
      <Route path="/:langCode/login" element={<LoginRoute />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            {/* 旧URLは日本語版の管理画面へ遷移する */}
            <Navigate to="/jp/admin/faq" replace />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/:adminTab"
        element={
          <AdminRoute>
            <LegacyAdminTabRedirect />
          </AdminRoute>
        }
      />
      <Route
        path="/:langCode/admin"
        element={
          <AdminRoute>
              {/* 管理画面の初期表示はFAQページへ遷移する */}
            <AdminIndexRedirect />
          </AdminRoute>
        }
      />
      <Route
        path="/:langCode/admin/:adminTab"
        element={
          <AdminRoute>
            {/* 選択された管理機能に応じてURLを切り替える */}
            {/* Keeps a failed admin chunk fetch from blanking the whole app */}
            <ErrorBoundary>
              <Suspense fallback={<LoadingScreen />}>
                <AdminApp />
              </Suspense>
            </ErrorBoundary>
          </AdminRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {/* チャット画面の初期表示は日本語URLへ遷移する */}
            <Navigate to="/jp" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:langCode/chat/:sessionId"
        element={
          <ProtectedRoute>
            <ChatApp />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:langCode"
        element={
          <ProtectedRoute>
            <ChatApp />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
